import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { PageHeader } from "../components/shared";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { X } from "lucide-react";

const ROLES = ["OWNER", "SUPER_ADMIN", "BRANCH_MANAGER", "RECEPTION", "STAFF", "ACCOUNTANT"];

export default function AuditLog() {
  const { user } = useAuth();
  const { branches } = useBranch();
  const isAdmin = user?.role === "OWNER" || user?.role === "SUPER_ADMIN";
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ user_id: "all", role: "all", branch_id: "all", start: "", end: "" });

  useEffect(() => { if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [isAdmin]);

  const load = useCallback(() => {
    const params = {};
    if (f.user_id !== "all") params.user_id = f.user_id;
    if (f.role !== "all") params.role = f.role;
    if (f.branch_id !== "all") params.branch_id = f.branch_id;
    if (f.start) params.start = f.start;
    if (f.end) params.end = f.end;
    api.get("/audit-logs", { params }).then((r) => setRows(r.data));
  }, [f]);
  useEffect(() => { load(); }, [load]);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";
  const clear = () => setF({ user_id: "all", role: "all", branch_id: "all", start: "", end: "" });
  const active = f.user_id !== "all" || f.role !== "all" || f.branch_id !== "all" || f.start || f.end;

  return (
    <div>
      <PageHeader title="Audit Log" subtitle="Every sensitive change is recorded — who, what, before/after & when" />

      <Card className="p-4 bg-card border-border mb-4">
        <div className="flex flex-wrap items-end gap-3">
          {isAdmin && (
            <div className="min-w-[180px]">
              <label className="text-xs text-muted-foreground">User</label>
              <Select value={f.user_id} onValueChange={(v) => setF({ ...f, user_id: v })}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="audit-filter-user"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {isAdmin && (
            <div className="min-w-[150px]">
              <label className="text-xs text-muted-foreground">Role</label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="audit-filter-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {isAdmin && (
            <div className="min-w-[180px]">
              <label className="text-xs text-muted-foreground">Branch</label>
              <Select value={f.branch_id} onValueChange={(v) => setF({ ...f, branch_id: v })}>
                <SelectTrigger className="bg-secondary mt-1" data-testid="audit-filter-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <label className="text-xs text-muted-foreground">From</label>
            <Input type="date" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} className="bg-secondary mt-1 w-40" data-testid="audit-filter-start" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">To</label>
            <Input type="date" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} className="bg-secondary mt-1 w-40" data-testid="audit-filter-end" />
          </div>
          {active && <Button variant="ghost" onClick={clear} className="text-muted-foreground" data-testid="audit-filter-clear"><X size={14} className="mr-1" /> Clear</Button>}
          <span className="ml-auto text-sm text-primary" data-testid="audit-count">{rows.length} record{rows.length === 1 ? "" : "s"}</span>
        </div>
      </Card>

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>When</TableHead><TableHead>User</TableHead><TableHead>Role</TableHead>
              <TableHead>Branch</TableHead><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>Reason</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="border-border" data-testid={`audit-row-${r.id}`}>
                <TableCell className="text-muted-foreground text-xs">{new Date(r.timestamp).toLocaleString("en-IN")}</TableCell>
                <TableCell>{r.user_name || r.user_email}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.user_role || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.branch_id ? branchName(r.branch_id) : "—"}</TableCell>
                <TableCell><Badge variant="outline" className="border-primary text-primary">{r.action}</Badge></TableCell>
                <TableCell>{r.entity}</TableCell>
                <TableCell className="text-muted-foreground text-xs max-w-[200px] truncate">{r.reason || "—"}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">No audit records match the filters.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
